import * as types from './types';
import * as R from 'ramda';
import { log } from '../../../utils/jsUtils';

const initialState = {
  requests: [],
  units: [],
  totalRequests: 0,
  filterSelections: {
    propertyOptions: [],
    staffOptions: [],
    tenantOptions: [],
    tagOptions: [],
    unreadMessagesOnlyOptions: [],
    pageNb: 0,
    itemsPerPage: 50 // Keep consistent with backend expectation (see initialState.js)
  },
  filterOptions: {
    propertyOptions: [],
    staffOptions: [],
    tenantOptions: [],
    tagOptions: [],
    unreadMessagesOnlyOptions: []
  },
  filterSearchText: '',
  isFeedLive: true
};

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case types.SET_REQUESTS:
      log('[feed/reducer] SET_REQUESTS action received', {
        requestCount: action.payload.requests.length,
        totalRequests: action.payload.totalRequests,
        timestamp: new Date().toISOString()
      });
      return {
        ...state,
        requests: action.payload.requests,
        totalRequests: action.payload.totalRequests
      };
    case types.SET_UNITS:
      return {
        ...state,
        units: action.payload
      };
    case types.SET_FILTER_SEARCH_TEXT:
      return {
        ...state,
        filterSearchText: action.payload
      };
    case types.SET_FILTER_PAGE_NUMBER:
      return {
        ...state,
        filterSelections: {
          ...state.filterSelections,
          pageNb: action.payload
        }
      };
    case types.SET_FILTER_SELECTIONS:
      return {
        ...state,
        filterSelections: {
          ...state.filterSelections,
          ...action.payload,
          pageNb: action.payload.pageNb !== undefined ? action.payload.pageNb : state.filterSelections.pageNb || 0,
          itemsPerPage: action.payload.itemsPerPage !== undefined ? action.payload.itemsPerPage : state.filterSelections.itemsPerPage || 50
        }
      };
    case types.SET_FILTER_OPTIONS:
      return {
        ...state,
        filterOptions: action.payload
      };
    case types.SET_EXPORT_CSV_REQUESTS:
      return {
        ...state,
        exportCSVRequests: action.payload
      };
    case types.SET_PRINT_REQUESTS:
      return {
        ...state,
        printRequests: action.payload
      };
    case types.SET_FEED_LIVE:
      return {
        ...state,
        isFeedLive: action.payload
      };
    case types.SET_FEED_INITIALIZED:
      return {
        ...state,
        feedInitialized: action.payload
      };
    case types.UPDATE_REQUESTS:
      // Log the update for debugging purposes
      log('[feed/reducer] UPDATE_REQUESTS received', {
        updateCount: action.payload.length,
        updateTime: new Date().toISOString(),
        updatedIds: action.payload.map(req => req._id).slice(0, 5), // Show first 5 for brevity
        firstUpdate: action.payload[0] ? {
          id: action.payload[0]._id,
          hasLookupTenant: !!action.payload[0].lookupTenant,
          tagsCount: action.payload[0].lookupTenant?.Tags?.length,
          customerName: action.payload[0].lookupTenant?.FirstName,
          tags: action.payload[0].lookupTenant?.Tags
        } : null
      });

      // Create a map of updated requests for faster lookup
      const updatedRequestsMap = action.payload.reduce((map, req) => {
        map[req._id] = req;
        return map;
      }, {});
      
      // Debug: Sample a customer being updated to check their tags
      const sampleRequest = action.payload.find(req => req.lookupTenant?.Tags);
      if (sampleRequest && process.env.NODE_ENV !== 'production') {
        const currentRequest = state.requests.find(req => req._id === sampleRequest._id);
        if (currentRequest) {
          log('[feed/reducer] Tag comparison for sample customer:', {
            customerId: sampleRequest.lookupTenant?._id,
            customerName: sampleRequest.lookupTenant?.FirstName,
            beforeTags: currentRequest.lookupTenant?.Tags || [],
            afterTags: sampleRequest.lookupTenant?.Tags || [],
            removed: (currentRequest.lookupTenant?.Tags || []).length > (sampleRequest.lookupTenant?.Tags || []).length,
            added: (currentRequest.lookupTenant?.Tags || []).length < (sampleRequest.lookupTenant?.Tags || []).length,
            updateTime: new Date().toISOString()
          });
        }
      }
      
      // Track which IDs are already in state for detecting new requests
      const existingIds = new Set(state.requests.map(req => req._id));

      // Create new arrays with updated requests
      const updatedRequests = state.requests.map(req => {
        if (updatedRequestsMap[req._id]) {
          if (process.env.NODE_ENV !== 'production' &&
              req.lookupTenant?.Tags &&
              updatedRequestsMap[req._id].lookupTenant?.Tags &&
              req.lookupTenant.Tags.length !== updatedRequestsMap[req._id].lookupTenant.Tags.length) {
            // Log tag changes for debugging
            log(`[feed/reducer] Tags changed for request ${req._id}:`, {
              before: req.lookupTenant.Tags,
              after: updatedRequestsMap[req._id].lookupTenant.Tags,
              customerId: req.lookupTenant._id,
              customerName: req.lookupTenant.FirstName,
              updateTime: new Date().toISOString()
            });
          }
          return updatedRequestsMap[req._id];
        }
        return req;
      });

      // Add any NEW requests that weren't already in state.requests
      // This fixes the issue where /member/xxx dispatches a request to Redux
      // but it never gets added because .map() only iterates existing items
      const newRequests = action.payload.filter(req => !existingIds.has(req._id));
      if (newRequests.length > 0) {
        log('[feed/reducer] Adding new requests to Redux:', {
          count: newRequests.length,
          ids: newRequests.map(r => r._id),
          updateTime: new Date().toISOString()
        });
      }

      const finalRequests = [...updatedRequests, ...newRequests];

      // Log the results
      log('[feed/reducer] After UPDATE_REQUESTS', {
        updateTime: new Date().toISOString(),
        oldRequestsCount: state.requests.length,
        updatedCount: updatedRequests.length,
        newCount: newRequests.length,
        finalCount: finalRequests.length,
        sampleTags: finalRequests[0]?.lookupTenant?.Tags?.length || 0,
        state: 'UPDATED' // Indicator for logs to find this message easily
      });

      return {
        ...state,
        requests: finalRequests
      };
    default:
      return state;
  }
}